import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;
const POLL_INTERVAL = 2500;

const CheckoutSuccess = () => {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: "pending" });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "no_session" });
      return;
    }
    let alive = true;
    let count = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!alive) return;
        setState(data);
        setTries(count + 1);
        if (data.payment_status === "paid" || data.payment_status === "failed" || data.payment_status === "expired") return;
        count += 1;
        if (count >= MAX_POLLS) return;
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (!alive) return;
        setState({ status: "error", payment_status: "error" });
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const failed = ["failed", "expired", "error", "no_session"].includes(state.payment_status);
  const pending = !paid && !failed;

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
      <div className="w-full max-w-lg text-center">
        <Link to="/" className="inline-flex items-center gap-4 mb-10">
          <img src={LIFE_LOGO} alt="LIFE" className="h-14 w-14 object-contain" />
          <div className="w-px h-12 bg-nex-border" />
          <NexWordmark size="text-3xl" />
        </Link>

        <div className="card-industrial corner-tick p-10">
          {pending && (
            <>
              <Loader2 size={40} className="mx-auto text-nex-gold animate-spin mb-6" />
              <div className="mono-label mb-3">// CONFIRMING PAYMENT</div>
              <h1 className="serif-head text-3xl mb-4">Verifying with Stripe...</h1>
              <p className="text-nex-chrome text-sm">This usually takes 2–5 seconds. Hold tight.</p>
              <p className="mono-body text-[10px] mt-4">Attempt {tries}/{MAX_POLLS}</p>
            </>
          )}
          {paid && (
            <>
              <CheckCircle2 size={44} className="mx-auto text-emerald-400 mb-6" />
              <div className="mono-label mb-3">// PAYMENT CONFIRMED</div>
              <h1 className="serif-head text-3xl mb-5">You're officially in.</h1>
              <p className="text-nex-chrome leading-relaxed mb-8">
                Your consultation fee is paid. William starts your <span className="text-nex-silver">48-hour prototype build</span> immediately. You'll receive a private preview link within one business day.
              </p>
              {process.env.REACT_APP_CAL_COM_URL ? (
                <div className="border border-nex-gold/40 bg-nex-gold/5 p-6 mb-6 text-left">
                  <div className="mono-label mb-3 text-center">// LOCK IN YOUR KICKOFF CALL</div>
                  <p className="text-nex-chrome text-sm mb-4 text-center">While William is building, grab a 30-min call slot to walk through your prototype together.</p>
                  <a
                    href={process.env.REACT_APP_CAL_COM_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="cal-book-btn"
                    className="tick-btn-primary w-full justify-center"
                  >
                    Book Your Kickoff Call →
                  </a>
                </div>
              ) : null}
              <Link to="/" data-testid="checkout-home" className="tick-btn-secondary inline-flex">← Back to NEX</Link>
            </>
          )}
          {failed && (
            <>
              <XCircle size={40} className="mx-auto text-red-400 mb-6" />
              <div className="mono-label mb-3">// PAYMENT UNCONFIRMED</div>
              <h1 className="serif-head text-3xl mb-4">Something went sideways.</h1>
              <p className="text-nex-chrome text-sm mb-6">
                Your payment may still be processing. If you were charged, don&apos;t worry — William will reach out within a business day.
              </p>
              <Link to="/" className="tick-btn-secondary inline-flex">← Back to NEX</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
